import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  type ChangeEventHandler,
  type ComponentProps,
  type FocusEventHandler,
  type InputHTMLAttributes,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "../buttons";
import { cn } from "../utils/cn";
import type { TransformFunction, ValidationFunction } from "./types";
import { DEFAULT_TYPING_TIMEOUT, isString } from "./utils";

interface ButtonProps {
  onClick: (value: string) => void;
  label: string;
  type?: ComponentProps<typeof Button>["type"];
}

interface Props
  extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  leftIcon?: IconProp;
  rightButton?: ButtonProps;
  label?: string;
  onFinishWriting?: (value: string) => void;
  timeout?: number;
  transformValue?: TransformFunction;
  validate?: ValidationFunction;
  inputClassName?: string;
  onFocus?: () => void;
  editable?: boolean;
  onClick?: () => void;
}

export interface InputRef {
  clear: () => void;
  value: string;
}

type HTMLGeneralInput = HTMLInputElement | HTMLTextAreaElement;

export const EditorInput = forwardRef<InputRef, Props>(
  (
    {
      label,
      leftIcon,
      className,
      onFinishWriting,
      value: initialValue,
      onBlur,
      onChange,
      disabled,
      timeout = DEFAULT_TYPING_TIMEOUT,
      transformValue,
      validate = () => true,
      rightButton,
      onFocus,
      inputClassName,
      editable = true,
      onClick,
      ...props
    },
    ref,
  ) => {
    const InputComponent = "input";

    if (initialValue != null && !isString(initialValue))
      throw new Error("value must be a string");

    const value = useRef(initialValue ?? "");
    const [valueState, setValueState] = useState(value.current);
    const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
      null,
    );
    const [isValid, setIsValid] = useState(validate(value.current));
    const [isFocused, setIsFocused] = useState(false);
    const didChange = useRef(false);

    useImperativeHandle(ref, () => ({
      clear: () => {
        value.current = "";
        setValueState("");
        setIsValid(validate(value.current));
      },
      value: value.current,
    }));

    useEffect(() => {
      if (initialValue != null && initialValue !== value.current) {
        value.current = initialValue;
        setValueState(initialValue);
        setIsValid(validate(initialValue));
      }
    }, [validate, initialValue]);

    const handleFinishWriting = useCallback(
      (value: string) => {
        const newValue = transformValue?.(value) ?? value;

        if (
          validate(newValue) &&
          initialValue !== newValue &&
          didChange.current
        )
          onFinishWriting?.(newValue);
        didChange.current = false;
      },
      [initialValue, onFinishWriting, transformValue, validate],
    );

    const handleFocus: FocusEventHandler<HTMLGeneralInput> = useCallback(
      (event) => {
        setIsFocused(true);
        if (onFocus) {
          onFocus();
        }
      },
      [onFocus],
    );

    const handleBlur: FocusEventHandler<HTMLGeneralInput> = useCallback(
      (event) => {
        setIsFocused(false);
        event.stopPropagation();
        handleFinishWriting(value.current);
        onBlur?.(event);
      },
      [handleFinishWriting, onBlur],
    );

    const handleChange: ChangeEventHandler<HTMLGeneralInput> = useCallback(
      (event) => {
        const newValue = event.target.value;
        value.current = newValue;
        setValueState(newValue);
        didChange.current = true;

        if (typingTimeout) clearTimeout(typingTimeout);
        const newTimeout = setTimeout(
          () => handleFinishWriting(newValue),
          timeout,
        );
        setTypingTimeout(newTimeout);
        setIsValid(validate(newValue));

        onChange?.(event);
      },
      [onChange, handleFinishWriting, typingTimeout, timeout, validate],
    );

    useEffect(() => {
      return () => {
        if (typingTimeout) clearTimeout(typingTimeout);
      };
    }, [typingTimeout]);

    return (
      <div className="relative flex flex-row items-center w-[100%]">
        <div
          className={cn(
            "absolute -left-2 w-1 h-full transition-all duration-300 rounded-md",
            isFocused ? "opacity-100 bg-gray-300" : "opacity-0 bg-transparent",
          )}
        />
        <div
          className={cn(
            "font-medium group flex gap-3 transition-all duration-300 w-full min-w-0",
            !isValid &&
              "border-red-3 bg-red-1 hover:bg-red-1 focus-within:bg-red-2 hover:focus-within:bg-red-2",
            onClick && [
              "cursor-pointer",
              "hover:font-bold focus-within:font-bold",
            ], // Only apply hover effects if onClick exists
            className,
          )}
          onClick={onClick}
        >
          {leftIcon && (
            <FontAwesomeIcon
              icon={leftIcon}
              className="mt-4"
              width={14}
              height={14}
            />
          )}
          <InputComponent
            className={cn(
              "bg-transparent outline-none flex-grow placeholder:text-gray resize-none",
              "transition-all duration-300",
              onClick && "pointer-events-none", // Keep this to prevent input interaction when clickable
              inputClassName,
            )}
            placeholder={label}
            disabled={disabled}
            {...props}
            value={valueState}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
          />
          {rightButton && (
            <Button
              disabled={disabled || !isValid}
              className="my-2"
              type={rightButton.type ?? "button"}
              onClick={(e) => {
                e.stopPropagation();
                rightButton.onClick(
                  transformValue?.(value.current) ?? value.current,
                );
              }}
            >
              {rightButton.label}
            </Button>
          )}
        </div>
      </div>
    );
  },
);

EditorInput.displayName = "EditorInput";
